import { Link } from "react-router-dom";
import { ArrowUpRight, Stethoscope, Building2, Users } from "lucide-react";
import { Button } from "../components/ui/button";

const ACRONYM = [
  {
    letter: "P",
    word: "Payment",
    desc: "Every dollar earned at the bedside, recovered. We make sure the care your team delivered is the care your group gets paid for.",
  },
  {
    letter: "R",
    word: "Resolution",
    desc: "Disputes closed decisively. We don't just file — we drive payer negotiations to fair, defensible outcomes that hold up under scrutiny.",
  },
  {
    letter: "I",
    word: "IDR",
    desc: "Federal Independent Dispute Resolution under the No Surprises Act. The arbitration process built to level the playing field — we make it work for you.",
  },
  {
    letter: "S",
    word: "System",
    desc: "Purpose-built software that scales with your practice. From a single ER group to a multi-state facility network, the platform handles the volume.",
  },
  {
    letter: "M",
    word: "Management",
    desc: "End-to-end handling — eligibility, negotiation, IDR filing, follow-through. Your clinicians stay focused on patients; we run the rest.",
  },
];

const WHO_WE_HELP = [
  {
    icon: Stethoscope,
    title: "Providers",
    desc: "Emergency physicians, anesthesiologists, radiologists, hospitalists, pathologists — anyone delivering care out-of-network deserves fair compensation. We make sure they get it.",
  },
  {
    icon: Building2,
    title: "Facilities",
    desc: "Hospitals, surgery centers, freestanding ERs, and air ambulance providers. We strengthen your revenue cycle so your facility stays sustainable and your doors stay open.",
  },
  {
    icon: Users,
    title: "Communities",
    desc: "When providers are paid fairly, practices stay viable, clinicians stay at the bedside, and patients keep access to the care their community needs.",
  },
];

const VALUES = [
  { t: "Provider-first", d: "Every decision starts with one question: does this help the provider get paid fairly? Nothing else takes priority." },
  { t: "Defensible math", d: "Every offer we make is supported by citations, benchmarks, and statistically valid comparables — built to withstand any IDRE review." },
  { t: "Aligned pricing", d: "Pay-on-results. We only win when you do, which means our incentives are identical to yours from day one." },
  { t: "Operational rigor", d: "Every case has a named owner, a documented playbook, and a full audit trail. No queues, no black boxes." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative bg-white">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20 relative">
          <div className="overline text-slate-500">Payment Resolution & IDR System Management</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light mt-4 text-slate-900 max-w-4xl leading-[1.05]">
            Fair compensation, <br />
            <span className="text-[#0A2540]">for the people who deliver care.</span>
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl">
            PRISM exists to help providers, facilities, and the communities they serve — by making
            sure that the care delivered is the care that gets paid for. We turn the No Surprises
            Act from a paperwork burden into your competitive advantage.
          </p>
        </div>
      </section>

      {/* ACRONYM INFOGRAPHIC */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="acronym-infographic">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">The PRISM acronym</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900 leading-tight">
                Five letters. <br />One promise.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-base text-slate-600 leading-relaxed">
                PRISM stands for <strong className="text-slate-900 font-semibold">Payment Resolution &amp; IDR System Management</strong> —
                the full stack of work it takes to get providers paid fairly under the No Surprises Act.
                Here's what each letter does for you.
              </p>
            </div>
          </div>

          {/* Letter blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {ACRONYM.map((a, i) => (
              <div
                key={a.letter}
                data-testid={`acronym-${a.letter.toLowerCase()}`}
                className="bg-white p-8 flex flex-col group hover:bg-slate-50 transition-colors relative"
              >
                <div className="font-display text-[120px] sm:text-[140px] leading-none font-light text-[#0A2540] tracking-tighter group-hover:text-[#2563EB] transition-colors">
                  {a.letter}
                </div>
                <div className="mt-4 overline text-slate-500">Stands for</div>
                <div className="font-display text-2xl font-medium text-slate-900 mt-2">{a.word}</div>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{a.desc}</p>
                <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-400 font-mono">
                  0{i + 1} / 05
                </div>
              </div>
            ))}
          </div>

          {/* Full expansion banner */}
          <div className="mt-12 border border-slate-300 bg-white p-8 lg:p-10 flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <span className="overline text-slate-500 mr-2">PRISM =</span>
            <span className="font-display text-2xl sm:text-3xl text-slate-900">
              <span className="text-[#2563EB] font-medium">P</span>ayment{" "}
              <span className="text-[#2563EB] font-medium">R</span>esolution{" "}
              <span className="text-slate-400">&amp;</span>{" "}
              <span className="text-[#2563EB] font-medium">I</span>DR{" "}
              <span className="text-[#2563EB] font-medium">S</span>ystem{" "}
              <span className="text-[#2563EB] font-medium">M</span>anagement
            </span>
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">Who we help</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                Built for providers. <br />Felt by communities.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-base text-slate-600 leading-relaxed">
                When providers and facilities are paid fairly for the care they deliver, healthcare
                works the way it's supposed to. Practices stay viable, clinicians stay at the
                bedside, and the communities they serve keep access to care.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {WHO_WE_HELP.map((w, i) => (
              <div
                key={w.title}
                data-testid={`who-we-help-${i}`}
                className="bg-white p-10 group hover:bg-slate-50 transition-colors"
              >
                <w.icon className="h-9 w-9 text-[#0A2540] group-hover:text-[#2563EB] transition-colors" />
                <div className="font-display text-2xl font-medium mt-6 text-slate-900">{w.title}</div>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <img
                src="https://images.unsplash.com/photo-1764885415480-558e5631d371?fm=jpg&q=60&w=1600&auto=format&fit=crop"
                alt="Modern hospital facility"
                className="w-full h-[480px] object-cover border border-slate-200"
              />
            </div>
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">Our mission</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-light mt-4 text-slate-900">
                Return underpaid dollars to the people who earned them.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                The No Surprises Act protects patients from surprise bills — that's a good thing.
                But the dispute process built around it is heavily tilted toward payers. Providers
                have a 30-day window, one offer, and limited support. PRISM levels that playing
                field, so the people delivering care aren't the ones left short.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline text-slate-500">What guides us</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                Four operating values.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                {VALUES.map((v, i) => (
                  <div key={v.t} className="bg-white p-8" data-testid={`value-${i}`}>
                    <div className="font-display text-xl font-medium text-slate-900">{v.t}</div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-blue-300">Our team</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4">
                Seasoned operators from the other side of the table.
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
                Most of our team has worked inside payer dispute departments — as auditors,
                healthcare regulatory attorneys, board-certified physicians, and credentialed
                actuaries. We know how payers staff, score, and settle disputes because we used
                to do it ourselves.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-white/15 bg-white/[0.04] p-8">
                <div className="overline text-blue-300">Backgrounds on our bench</div>
                <ul className="mt-6 space-y-3 text-slate-200 text-sm">
                  <li>· Former payer provider-disputes leads</li>
                  <li>· Healthcare regulatory attorneys</li>
                  <li>· Board-certified emergency &amp; anesthesia physicians</li>
                  <li>· Credentialed actuaries (FSA, ASA)</li>
                  <li>· Revenue cycle &amp; clearinghouse operators</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light text-slate-900 max-w-3xl mx-auto">
            Ready to see what fair compensation looks like for your group?
          </h2>
          <Button
            asChild
            data-testid="about-cta"
            className="mt-10 rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-12 px-6"
          >
            <Link to="/contact">Start with a free diagnostic <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
